/**
 * Kitchen-style order ticket — bitmap renderer.
 *
 * A back-of-house ticket: it shows what to make — items, modifiers, special
 * instructions, order notes — plus the order identifiers (order #, type, table,
 * customer). No prices, totals, QR code, or branding.
 *
 * Three sizes via `variant` (font scale only — same paper width):
 *   - 'small'  — original receipt-sized text ('normal' is a legacy alias)
 *   - 'medium' — 30% larger text
 *   - 'large'  — 60% larger text for the kitchen line
 */
import { getSaleOrderDetails, type CheckoutItemType } from '@am3nd-org/pointcraft-types';

import { staffMealFromOrderDetails } from 'src/operational-apps/kds/orders/staff-meal';

import { createReceiptCanvas } from './canvas-toolkit';
import type { PaperWidth, KitchenTicketOrder, OrderReceiptVariant } from './types';

/**
 * Minimum finished ticket length in 576-baseline dots (≈72 mm at the default paper
 * width) so a short ticket (e.g. a single item) still has enough paper below the cut
 * to grab and tear off. Longer tickets exceed this and are unaffected.
 */
const MIN_TICKET_HEIGHT = 576;

const SIZE_MULTIPLIERS: Record<OrderReceiptVariant, number> = {
  small: 1,
  medium: 1.3,
  large: 1.6,
  normal: 1,
};

// ────────────────────────────────────────────────────────────────────────────
// Data helpers
// ────────────────────────────────────────────────────────────────────────────

function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (value && typeof (value as any).toDate === 'function') return (value as any).toDate();
  return new Date();
}

/**
 * Treats blank values and the historical `'n/a'` sentinel as empty so those
 * rows are never printed (older saved metadata may still carry the sentinel).
 */
function clean(value?: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a') return undefined;
  return trimmed;
}

/** 'walk-in' (legacy) → 'to-go'; returns the human label for the order type. */
function orderTypeLabel(type?: string | null): string | undefined {
  if (!type) return undefined;
  const normalized = type === 'walk-in' ? 'to-go' : type;
  if (normalized === 'dine-in') return 'DINE-IN';
  if (normalized === 'to-go') return 'TO-GO';
  return normalized.toUpperCase();
}

function modifierNames(item: CheckoutItemType): string[] {
  const groups = item.metadata?.selectedModifierData ?? [];
  return groups
    .flatMap((group) => group.selected ?? [])
    .map((mod) => (mod.quantity && mod.quantity > 1 ? `${mod.name} ×${mod.quantity}` : mod.name))
    .filter(Boolean);
}

// ────────────────────────────────────────────────────────────────────────────
// Renderer
// ────────────────────────────────────────────────────────────────────────────

export function orderReceiptCanvas(
  order: KitchenTicketOrder,
  opts: { paperWidth?: PaperWidth; variant?: OrderReceiptVariant } = {}
): string {
  const sizeMul = SIZE_MULTIPLIERS[opts.variant ?? 'small'];
  const c = createReceiptCanvas({
    paperWidth: opts.paperWidth,
    sizeMul,
    minHeight: MIN_TICKET_HEIGHT,
  });
  const { px } = c;

  // ── Header ──────────────────────────────────────────────────────────────────
  const orderDetails = getSaleOrderDetails(order);
  const orderNo = orderDetails?.orderNumber ?? order.orderNumber ?? '';
  c.lines(`ORDER #${orderNo}`, 46, 700, 'center');
  c.space(px(6));

  // Order type between two rules (mirrors the sale receipt's channel band).
  const staffMeal = staffMealFromOrderDetails(orderDetails);
  const typeLabel = staffMeal ? 'STAFF MEAL' : orderTypeLabel(orderDetails?.customerType);
  c.rule();
  if (typeLabel) {
    c.lines(typeLabel, 30, 700, 'center');
    c.rule();
  }

  const table = clean(orderDetails?.tableNumber);
  if (table) c.lines(`Table ${table}`, 30, 600, 'center');

  const staffName = clean(staffMeal?.name);
  const customerName = clean(orderDetails?.customerName);
  if (staffName) c.lines(`Staff: ${staffName}`, 26, 600, 'center');
  else if (customerName) c.lines(`Customer: ${customerName}`, 26, 400, 'center');

  const date = toDate(order.createdAt ?? order.details?.date);
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  c.lines(`${dateStr} · ${timeStr}`, 24, 400, 'center');

  c.rule();

  // ── Items: qty × name, modifiers (indented), special instructions (indented) ─
  order.items.forEach((item, idx) => {
    c.lines(`${item.quantity}×  ${item.name}`, 30, 700, 'left');

    modifierNames(item).forEach((mod) => c.lines(`+ ${mod}`, 24, 400, 'left', px(26)));

    const note = clean(item.metadata?.specialInstructions);
    if (note) c.lines(`> ${note}`, 25, 600, 'left', px(26));

    if (idx < order.items.length - 1) c.space(px(14));
  });

  // ── Order-level notes ───────────────────────────────────────────────────────
  const orderNotes = clean(orderDetails?.orderNotes);
  if (orderNotes) {
    c.rule();
    c.lines('NOTES', 24, 700, 'left');
    c.space(px(4));
    c.lines(orderNotes, 26, 400, 'left');
  }

  return c.finish();
}
